import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import { cn } from '../../utils/cn';

interface CreateSwimlaneModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  onCreated: () => void;
}

export const CreateSwimlaneModal = ({ isOpen, onClose, boardId, onCreated }: CreateSwimlaneModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setTitle('');
    setColor('#6366f1');
    setIsDefault(false);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await kanbanApiService.createSwimlane(boardId, {
        title: trimmed,
        color: color.trim() || undefined,
        is_default: isDefault,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create swimlane.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const hexForPicker = color.startsWith('#') && color.length >= 7 ? color.slice(0, 7) : '#6366f1';

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-md"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title">New swimlane</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="create-swim-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              id="create-swim-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="create-swim-color" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Color
            </label>
            <div className="flex items-center gap-3">
              <input
                id="create-swim-color"
                type="color"
                value={hexForPicker}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-lg border border-glass-200/20 bg-transparent p-0.5"
                disabled={isSubmitting}
                aria-label="Swimlane color"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="glass-input flex-1 font-mono"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-glass-200 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="form-checkbox"
              disabled={isSubmitting}
            />
            Default swimlane
          </label>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
